// Sandboxed iframe preview of the rendered broadcast HTML. The server
// returns the full email document (DOCTYPE + head + body) so we render it
// via `srcDoc` inside a sandboxed iframe — same surface a recipient sees in
// their inbox.
//
// `sandbox="allow-same-origin"` permits the parent to read the iframe's
// contentDocument (needed for height measurement) but still blocks script
// execution, form submission, top-navigation, and popups — every dangerous
// capability is opt-in via additional `allow-*` tokens and we grant none.
// Email HTML doesn't run JS anyway.

import { useCallback, useRef, useState } from 'react';
import { Body, Caption, Dialog, DialogSection, Stack } from '@leanin/ui';

export interface BroadcastPreviewIframeProps {
  readonly html: string;
  /** When provided, the email's subject line renders above the preview. */
  readonly subject?: string;
  readonly open: boolean;
  readonly onClose: () => void;
}

export function BroadcastPreviewIframe({
  html,
  subject,
  open,
  onClose,
}: BroadcastPreviewIframeProps) {
  // Iframe height is driven by the email's own scrollHeight, measured on
  // load + on any subsequent reflow (e.g. web-font load shifting line
  // heights). Falls back to a sane min-height while measuring.
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  const measure = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const h = doc.documentElement.scrollHeight;
    if (Number.isFinite(h) && h > 0) setHeight(h);
  }, []);

  const handleLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (doc) {
      // The email template paints its body with a cream background by
      // design — that's what the recipient sees in their inbox. Inside a
      // modal preview, the cream creates a hard transition under the
      // dialog header (modal-white → cream-iframe → email-card-white,
      // three concentric surfaces). Override to transparent so the email
      // card sits directly on the modal's surface — one nested surface
      // instead of three.
      doc.body.style.backgroundColor = 'transparent';
    }
    measure();
    // The email's web fonts load async — re-measure once they settle so the
    // iframe height doesn't lock in at the pre-font fallback height.
    if (doc && doc.fonts && doc.fonts.ready) {
      void doc.fonts.ready.then(measure);
    }
  }, [measure]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Email preview"
      description="How recipients will see this email in their inbox."
      size="lg"
    >
      {subject !== undefined ? (
        <DialogSection>
          <Stack gap="xs">
            <Caption>Subject</Caption>
            <Body>{subject}</Body>
          </Stack>
        </DialogSection>
      ) : null}
      {/* Non-bleed DialogSection: its natural padding (16px vertical,
       * 24px horizontal) gives the header room to breathe at the bottom
       * AND insets the iframe from the modal edges — matching how every
       * other Dialog in the app looks. `scrollable` lets the section take
       * the remaining vertical space and scroll internally when an email
       * exceeds the modal's max-height. */}
      <DialogSection scrollable>
        <iframe
          ref={iframeRef}
          title="Email preview"
          srcDoc={html}
          sandbox="allow-same-origin"
          onLoad={handleLoad}
          style={{
            display: 'block',
            width: '100%',
            height: height ?? 400,
            border: 'none',
          }}
        />
      </DialogSection>
    </Dialog>
  );
}
